import { useState } from "react";
import { IDENTITY_TRANSFORM, MAX_SCALE, MIN_SCALE } from "./PannablePhoto";

export const ZOOM_STEP = 0.25;

// State for a pair comparison that has to outlive a mode switch. Switching
// between side-by-side, slider and fade unmounts PairCompare, so anything the
// user has set up — the fade position and the per-photo pan/zoom alignment —
// is held here, in the modal, instead of inside the view.
//
// Transforms are keyed by `${scope}:${side}` (side being "left" or "right"), so
// each scope — an angle, a pair of photos, whatever the caller partitions by —
// keeps its own pair of adjustments. `fade` and `fadeSide` are modal-wide and
// deliberately not scoped.
const usePairCompareState = (scope) => {
  const [transforms, setTransforms] = useState({});
  const [fade, setFade] = useState(50);
  // Which photo the pan/zoom gestures act on in fade mode (the two photos are
  // stacked, so only one can capture pointer events at a time).
  const [fadeSide, setFadeSide] = useState("right");

  const keyFor = (side) => `${scope}:${side}`;
  const getTransform = (side) => transforms[keyFor(side)] || IDENTITY_TRANSFORM;
  const setTransform = (side) => (next) =>
    setTransforms((m) => ({ ...m, [keyFor(side)]: next }));

  const stepZoom = (side, delta) => {
    const cur = getTransform(side);
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, cur.scale + delta));
    setTransform(side)({ ...cur, scale });
  };

  const isAdjusted = (side) => {
    const tr = transforms[keyFor(side)];
    return Boolean(tr) && tr !== IDENTITY_TRANSFORM;
  };

  const resetScope = () =>
    setTransforms((m) => {
      const next = { ...m };
      delete next[keyFor("left")];
      delete next[keyFor("right")];
      return next;
    });

  return {
    getTransform,
    setTransform,
    stepZoom,
    resetScope,
    isAnyAdjusted: isAdjusted("left") || isAdjusted("right"),
    fade,
    setFade,
    fadeSide,
    setFadeSide,
  };
};

export default usePairCompareState;
